/**
 * Per-aircraft sound sets — data, not code.
 *
 * CLAUDE.md's eighth non-negotiable says the core flight model contains zero aircraft-specific
 * `if` statements. The same rule applies here, and for the same reason: the moment the engine
 * voice asks "is this the Sparrow?" there is no path to three aircraft, let alone to a community
 * one. So every difference between a trainer, an aerobat and a fighter is a *number* in this
 * struct, and the synth reads the numbers.
 *
 * ## Derived first, declared second
 *
 * Most of a sound set is already implied by the physics config. The rated RPM is in the engine
 * block, the propeller diameter is in the propeller block, the aircraft's weight is in the mass
 * block, and the wing's stall angle is in its aerofoil table. Re-typing those into an audio
 * section would be four more places to get out of step with the aeroplane.
 *
 * So {@link soundConfigFor} derives everything it can and the optional `sound` block in the
 * aircraft JSON only carries what genuinely cannot be derived — how many cylinders the engine
 * has, how many blades the propeller has, whether a stall horn is fitted — plus level trims,
 * which are taste.
 *
 * See `src/aircraft/README.md` for the JSON schema.
 *
 * `updateFixed` / `updateFrame`: neither. Built once, when an aircraft is loaded.
 */

import { engineIdleSpeed, engineReference, type LoadedAircraft } from '../aircraft/config.js';

/** The kind of powerplant, which selects a voice rather than parameterising one. */
export type EngineVoiceKind = 'piston' | 'turbine';

/** Everything the audio system needs to know about one aircraft type. */
export interface AircraftSoundConfig {
  engineKind: EngineVoiceKind;
  /** Cylinders. Sets the firing order and therefore the engine's fundamental note. */
  cylinders: number;
  /** Strokes per cycle. 4 for everything this project will ever fly, but the maths is general. */
  strokes: number;
  /** Propeller blades. Sets the blade-pass tone. */
  bladeCount: number;
  propDiameterM: number;
  /** Idle shaft speed [rpm] — where the lope is deepest. */
  idleRpm: number;
  ratedRpm: number;
  ratedPowerW: number;

  /** Level trim for the engine voice, 1 = neutral. */
  engineTrim: number;
  /** Level trim for the airframe (wind, buffet) voice. */
  airframeTrim: number;

  /** Weight at which the rolling rumble is at full level [N] — the aircraft's own MTOW. */
  referenceWeightN: number;

  /** The wing's stall angle [rad]. Buffet and horn thresholds are fractions of it. */
  stallAlphaRad: number;
  /** Is a stall warning horn fitted? Trainers yes; a fly-by-wire fighter uses its alpha limiter. */
  stallHorn: boolean;
  /** Horn pitch [Hz]. A pneumatic reed sits around 3 kHz and is deliberately unpleasant. */
  hornHz: number;

  /**
   * Does the gear retract?
   *
   * The gear servo voice is silent unless this is true, because a sound for a mechanism the
   * aeroplane does not have is worse than no sound: it teaches the pilot a cue that means
   * nothing. The Sparrow is a fixed-gear trainer and leaves this false.
   */
  retractableGear: boolean;
}

/** The optional `sound` block in an aircraft's JSON. Everything omitted is derived. */
export interface AircraftSoundConfigJson {
  engineKind?: EngineVoiceKind;
  cylinders?: number;
  strokes?: number;
  bladeCount?: number;
  idleRpm?: number;
  engineTrim?: number;
  airframeTrim?: number;
  stallHorn?: boolean;
  hornHz?: number;
  retractableGear?: boolean;
}

/** Standard gravity [m/s²], for turning a mass into the weight the tyres carry. */
const G0 = 9.80665;

/**
 * Build a sound set for a loaded aircraft.
 *
 * The two derivations worth explaining:
 *
 *   - **Idle RPM** defaults to a quarter of rated, which is where `Propulsion`'s constructor also
 *     starts the shaft. 675 rpm on a 2700 rpm engine is a realistic light-aircraft idle, and
 *     keeping the two in step means the aeroplane does not spawn making a sound that says it is
 *     at a different power setting than it is.
 *   - **Stall alpha** comes from the wing panel's own aerofoil table — the angle at which that
 *     table's lift coefficient peaks — rather than from a constant. This is the number the
 *     aerodynamics genuinely stalls at, so the horn cannot be rigged to the wrong wing.
 */
export function soundConfigFor(aircraft: LoadedAircraft): AircraftSoundConfig {
  const json = aircraft.config.sound ?? {};
  const engine = aircraft.config.engine;
  const reference = engineReference(engine);

  return {
    engineKind: json.engineKind ?? 'piston',
    cylinders: json.cylinders ?? 4,
    strokes: json.strokes ?? 4,
    bladeCount: json.bladeCount ?? 2,
    // A jet has no propeller, so it has no blade-pass tone and no tip Mach. Zero is the honest
    // answer and the turbine voice does not ask for it.
    propDiameterM: aircraft.config.propeller?.diameterM ?? 0,
    idleRpm: json.idleRpm ?? engineIdleSpeed(engine),
    ratedRpm: reference.speedRedline,
    ratedPowerW: reference.fullPowerReference,
    engineTrim: json.engineTrim ?? 1,
    airframeTrim: json.airframeTrim ?? 1,
    referenceWeightN: aircraft.config.mass.maxTakeoffKg * G0,
    stallAlphaRad: wingStallAlphaRad(aircraft),
    stallHorn: json.stallHorn ?? true,
    hornHz: json.hornHz ?? 3_050,
    retractableGear: json.retractableGear ?? false,
  };
}

/**
 * The stall angle of the aircraft's main lifting surface [rad].
 *
 * "Main" is the largest horizontal panel, which is the wing on every layout this simulator will
 * see — a canard aircraft would need this to be smarter, and would say so by failing the gate
 * rather than by sounding subtly wrong. Falls back to 16° if an aircraft somehow has no
 * horizontal surface at all, which the config validator would have rejected first.
 */
function wingStallAlphaRad(aircraft: LoadedAircraft): number {
  let best: number | null = null;
  let bestArea = 0;
  for (const panel of aircraft.panels) {
    if (panel.kind !== 'horizontal') continue;
    if (panel.areaM2 <= bestArea) continue;
    bestArea = panel.areaM2;
    best = panel.stallAlphaRad;
  }
  return best ?? (16 * Math.PI) / 180;
}
